import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Avatar,
  Badge,
  Progress,
  IconButton,
  Tooltip,
  useToast,
  useColorModeValue,
  Container,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  AspectRatio,
  Image,
  useBreakpointValue,
  Spinner,
  Center,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Link,
} from '@chakra-ui/react';
import {
  FiPlay,
  FiPlayCircle,
  FiClock,
  FiUsers,
  FiStar,
  FiCheckCircle,
  FiLock,
  FiUnlock,
  FiFileText,
  FiPaperclip,
  FiFile,
  FiDownload,
  FiChevronRight,
  FiChevronLeft,
  FiArrowRight,
  FiBookOpen,
  FiBook,
  FiLayers
} from 'react-icons/fi';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { fetchCourseById } from '../services/courseService';
import VideoPlayer from '../shared/videoplayer';
import axiosInstance from '../services/axiosInstance';
import Sidebar from '../shared/Sidebar';
import { useSidebar } from '../utils/SidebarContext';
import AuthContext from '../services/AuthContext';

const getChapterColorScheme = () => ({
  chapterBg: "blue.50",
  chapterBorder: "blue.200",
  chapterText: "blue.800",
  partBg: "gray.50",
  partBorder: "gray.200",
  videoBg: "white",
  selectedVideoBg: "blue.50",
  selectedVideoBorder: "blue.400",
  attachmentBg: "teal.50",
  attachmentIcon: "teal.500"
});

const AttachmentItem = ({ attachment, onDownload }) => {
  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop().toLowerCase();
    if (ext === 'pdf') return FiFileText;
    if (ext === 'doc' || ext === 'docx') return FiFile;
    return FiPaperclip;
  };

  return (
    <Box
      p={3}
      bg="gray.50"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      _dark={{ bg: "gray.700", borderColor: "gray.600" }}
      _hover={{ borderColor: "blue.400", bg: "blue.50", _dark: { bg: "gray.600" } }}
      transition="all 0.2s"
      cursor="pointer"
      onClick={() => onDownload(attachment)}
      mb={2}
    >
      <HStack spacing={4}>
        <Center w="40px" h="40px" borderRadius="md" bg="blue.100" color="blue.600">
          <Icon as={getFileIcon(attachment.nom)} boxSize={5} />
        </Center>
        <VStack align="flex-start" spacing={0} flex="1">
          <Text fontSize="sm" fontWeight="600" color="gray.800" _dark={{ color: "gray.100" }} noOfLines={1}>
            {attachment.nom}
          </Text>
          <Text fontSize="xs" color="gray.500">مرفق</Text>
        </VStack>
        <IconButton
          icon={<FiDownload />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          aria-label="تحميل"
        />
      </HStack>
    </Box>
  );
};

const CourseDetailsContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext(AuthContext);
  const { isOpen } = useSidebar();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const contentMarginLeft = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const [courseData, setCourseData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [completedVideos, setCompletedVideos] = useState(new Set());
  const [courseProgress, setCourseProgress] = useState(0);

  const [selectedNavChapter, setSelectedNavChapter] = useState(null);
  const [selectedNavPart, setSelectedNavPart] = useState(null);
  const [mobileActivePane, setMobileActivePane] = useState('chapters');

  const [homeworkPhotos, setHomeworkPhotos] = useState([]);  // Array of File objects
  const [myHomeworkReply, setMyHomeworkReply] = useState(null);
  const [allHomeworkReplies, setAllHomeworkReplies] = useState([]);
  const [isSubmittingHomework, setIsSubmittingHomework] = useState(false);
  const [isLoadingHomework, setIsLoadingHomework] = useState(false);

  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);

  const playerRef = useRef(null);

  useEffect(() => {
    fetchCourseData();
  }, [id]);



  useEffect(() => {
    if (selectedVideo?._id && courseData) {
      fetchHomeworkData(selectedVideo._id);
    }
  }, [selectedVideo, courseData]);

  const fetchCourseData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchCourseById(id);
      setCourseData(response.data);

      const totalVideos = response.data.chapitres?.reduce((acc, ch) =>
        acc + (ch.parties?.reduce((pAcc, p) => pAcc + (p.videos?.length || 0), 0) || 0), 0) || 0;
      const completed = Math.floor(totalVideos * 0.4);
      setCourseProgress(totalVideos > 0 ? (completed / totalVideos) * 100 : 0);

      // Set initial video and navigation states
      if (response.data.chapitres?.length > 0) {
        let firstOpenChapter = response.data.chapitres.findIndex(c => c.isOpen);
        if (firstOpenChapter === -1) firstOpenChapter = 0;

        setSelectedNavChapter(firstOpenChapter);
        if (response.data.chapitres[firstOpenChapter].parties?.length > 0) {
          setSelectedNavPart(0);
          if (response.data.chapitres[firstOpenChapter].parties[0].videos?.length > 0) {
            const firstVideo = response.data.chapitres[firstOpenChapter].parties[0].videos[0];
            setSelectedVideo({ ...firstVideo, chapterIndex: firstOpenChapter, partIndex: 0, videoIndex: 0 });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast({ title: 'خطأ', description: 'فشل تحميل بيانات الدورة', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHomeworkData = async (videoId) => {
    if (!videoId || !courseData?._id || !user) return;
    setIsLoadingHomework(true);
    try {
      if (user.role === 'student') {
        const res = await axiosInstance.get(`/video-homework/${videoId}/my-reply`, {
          params: { courseId: courseData._id }
        });
        setMyHomeworkReply(res.data.data);
      } else if (user.role === 'admin' || user.role === 'instructor') {
        const res = await axiosInstance.get(`/video-homework/${videoId}/replies`, {
          params: { courseId: courseData._id }
        });
        setAllHomeworkReplies(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching homework:', error);
    } finally {
      setIsLoadingHomework(false);
    }
  };

  const handleHomeworkSubmit = async () => {
    if (!selectedVideo || !courseData || !homeworkPhotos.length) return;
    setIsSubmittingHomework(true);
    try {
      const formData = new FormData();
      formData.append('videoId', selectedVideo._id);
      formData.append('courseId', courseData._id);
      formData.append('videoName', selectedVideo.nomvideo || 'فيديو بدون اسم');
      for (let i = 0; i < homeworkPhotos.length; i++) {
        formData.append('photos', homeworkPhotos[i]);
      }
      const res = await axiosInstance.post('/video-homework/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'تم', description: res.data.message, status: 'success', duration: 3000, isClosable: true });
      setHomeworkPhotos([]);
      fetchHomeworkData(selectedVideo._id);
    } catch (error) {
      toast({ title: 'خطأ', description: error.response?.data?.message || 'تعذر إرسال الواجب', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmittingHomework(false);
    }
  };

  const handleSendSms = async (sendTo) => {
    if (!selectedVideo || !courseData) return;
    setIsSendingSms(true);
    try {
      const res = await axiosInstance.post('/video-homework/send-sms', {
        videoId: selectedVideo._id,
        courseId: courseData._id,
        videoName: selectedVideo.title || selectedVideo.nomvideo || 'فيديو الدورة',
        sendTo
      });
      toast({ title: 'النتيجة', description: res.data.message, status: res.data.success ? 'success' : 'warning', duration: 4000, isClosable: true });
      setIsSmsModalOpen(false);
    } catch (error) {
      toast({ title: 'خطأ', description: error.response?.data?.message || 'فشل في الإرسال', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleVideoSelect = (video, chapterIndex, partIndex, videoIndex) => {
    setSelectedVideo({ ...video, chapterIndex, partIndex, videoIndex });
    setSelectedNavChapter(chapterIndex);
    setSelectedNavPart(partIndex);
    setCompletedVideos(prev => {
      const newSet = new Set(prev);
      newSet.add(`${chapterIndex}-${partIndex}-${videoIndex}`);
      return newSet;
    });

    // Deep link attempt (only if video has a URL, is responsive, and NOT on mobile)
    if (video?.url && !isMobile) {
      (async () => {
        try {
          const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
          const videoUrl = video.url;
          const fullVideoUrl = `${baseUrl}${videoUrl.startsWith('/') ? '' : '/'}${videoUrl}`;

          const response = await fetch(fullVideoUrl, { method: 'GET' });
          if (response.ok) {
            const deepLink = `cobra-edu://open?url=${fullVideoUrl}`;
            window.location.href = deepLink;
          }
        } catch (e) {
          console.warn('Video URL validation failed:', e);
        }
      })();
    }

    if (isMobile) {
      setMobileActivePane('player');
    } else {
      setTimeout(() => {
        playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleNextVideo = () => {
    if (!selectedVideo || !courseData) return;
    const { chapterIndex, partIndex, videoIndex } = selectedVideo;
    const chapter = courseData.chapitres[chapterIndex];
    if (!chapter) return;
    const part = chapter.parties[partIndex];
    if (!part) return;

    if (videoIndex < part.videos.length - 1) {
      handleVideoSelect(part.videos[videoIndex + 1], chapterIndex, partIndex, videoIndex + 1);
    } else if (partIndex < chapter.parties.length - 1) {
      const nextPart = chapter.parties[partIndex + 1];
      if (nextPart.videos && nextPart.videos.length > 0) {
        handleVideoSelect(nextPart.videos[0], chapterIndex, partIndex + 1, 0);
      }
    } else if (chapterIndex < courseData.chapitres.length - 1) {
      const nextChapter = courseData.chapitres[chapterIndex + 1];
      if (nextChapter.isOpen && nextChapter.parties.length > 0 && nextChapter.parties[0].videos.length > 0) {
        handleVideoSelect(nextChapter.parties[0].videos[0], chapterIndex + 1, 0, 0);
      }
    }
  };

  const handlePreviousVideo = () => {
    if (!selectedVideo || !courseData) return;
    const { chapterIndex, partIndex, videoIndex } = selectedVideo;

    if (videoIndex > 0) {
      const part = courseData.chapitres[chapterIndex].parties[partIndex];
      handleVideoSelect(part.videos[videoIndex - 1], chapterIndex, partIndex, videoIndex - 1);
    } else if (partIndex > 0) {
      const prevPart = courseData.chapitres[chapterIndex].parties[partIndex - 1];
      if (prevPart.videos.length > 0) {
        handleVideoSelect(prevPart.videos[prevPart.videos.length - 1], chapterIndex, partIndex - 1, prevPart.videos.length - 1);
      }
    } else if (chapterIndex > 0) {
      const prevChapter = courseData.chapitres[chapterIndex - 1];
      if (prevChapter.isOpen && prevChapter.parties.length > 0) {
        const lastPart = prevChapter.parties[prevChapter.parties.length - 1];
        if (lastPart.videos.length > 0) {
          handleVideoSelect(lastPart.videos[lastPart.videos.length - 1], chapterIndex - 1, prevChapter.parties.length - 1, lastPart.videos.length - 1);
        }
      }
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    try {
      if (!attachment.url) return;
      const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
      const fullUrl = `${baseUrl}${attachment.url.startsWith('/') ? '' : '/'}${attachment.url}`;
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.nom || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const baseUrl = process.env.REACT_APP_API_URL || 'https://api.cobra-education.com';
      const fullUrl = `${baseUrl}${attachment.url.startsWith('/') ? '' : '/'}${attachment.url}`;
      window.open(fullUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color="gray.500" fontWeight="600">جاري تحميل الدورة...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!courseData) {
    return (
      <Flex h="100vh" align="center" justify="center" bg={bgColor} dir="rtl">
        <VStack spacing={6}>
          <Icon as={FiBook} color="red.400" boxSize={16} />
          <Heading color="gray.700">الدورة غير موجودة</Heading>
          <Button as={RouterLink} to="/courses" colorScheme="blue" size="lg" borderRadius="full">العودة للرئيسية</Button>
        </VStack>
      </Flex>
    );
  }

  // Mobile: determine which pane counts as "showing navigation"
  const showingNavPane = isMobile && (mobileActivePane === 'chapters' || mobileActivePane === 'parts' || mobileActivePane === 'videos');

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif" bg={bgColor} minH="100vh">
      <Flex minH="100vh" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          ml={contentMarginLeft}
          transition="margin-left 0.3s"
          display="flex"
          flexDirection="column"
          minH="100vh"
          pb={{ base: showingNavPane ? 0 : '60px', lg: 0 }}
        >

          {/* Header Bar */}
          <Box bg="white" _dark={{ bg: "gray.800" }} borderBottom="1px solid" borderColor={borderColor} p={4} shadow="sm" zIndex={10}>
            <HStack justify="space-between" maxW="1600px" mx="auto">
              <HStack spacing={4}>
                <Heading size="md" color="gray.800" _dark={{ color: "white" }} noOfLines={1}>{courseData.name}</Heading>
                <Badge colorScheme="blue" display={{ base: "none", md: "inline-block" }} px={3} py={1} borderRadius="full">{courseData.category}</Badge>
              </HStack>
              {/* Progress */}
              <HStack spacing={4} display={{ base: "none", md: "flex" }}>
                <VStack align="flex-end" spacing={0}>
                  <Text fontSize="xs" fontWeight="700" color="gray.500">معدل التقدم</Text>
                  <Text fontSize="sm" fontWeight="700" color="blue.600">{Math.round(courseProgress)}%</Text>
                </VStack>
                <Box w="120px">
                  <Progress value={courseProgress} size="sm" colorScheme="blue" borderRadius="full" />
                </Box>
              </HStack>
            </HStack>
          </Box>

          {/* Main Layout */}
          <Flex
            flex="1"
            maxW="1600px"
            mx="auto"
            w="full"
            direction="column"
            overflow="visible"
            h="auto"
          >

            {/* Course Content Multi-pane Sidebar */}
            <Flex
              w={{ base: 'full', lg: 'auto' }}
              maxW={{ lg: '1000px' }}
              gap={0}
              direction={{ base: 'column', lg: 'row' }}
              borderRight={{ base: 'none', lg: "1px solid" }}
              borderColor={borderColor}
              bg={bgColor}
              h={{ base: 'auto', lg: 'auto' }}
              minH={{ lg: 'auto' }}
              overflow="visible"
              shadow={{ base: "none", lg: "-4px 0 15px rgba(0,0,0,0.02)" }}
              zIndex={5}
              display={{ base: mobileActivePane === 'player' ? 'none' : 'flex', lg: 'flex' }}
            >

              {/* Column 1: Chapters */}
              {(!isMobile || mobileActivePane === 'chapters') && (
                <Box
                  w={{ base: 'full', lg: '300px' }}
                  bg={cardBg}
                  display="flex"
                  flexDirection="column"
                  borderLeft={{ base: 'none', lg: "1px solid" }}
                  borderColor={borderColor}
                  h="auto"
                >
                  <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
                    <Heading size="md" color={textColor}>الفصول</Heading>
                  </Box>
                  <Box
                    flex="1"
                    overflowY="visible"
                    pt={2}
                    pb={{ base: '80px', lg: 0 }}
                  >
                    <VStack align="stretch" spacing={0}>
                      {courseData.chapitres?.map((chapter, cIdx) => (
                        <Box
                          key={cIdx}
                          px={4} py={4}
                          cursor={chapter.isOpen ? "pointer" : "not-allowed"}
                          opacity={chapter.isOpen ? 1 : 0.6}
                          bg={selectedNavChapter === cIdx ? 'blue.50' : 'transparent'}
                          _dark={{ bg: selectedNavChapter === cIdx ? 'blue.900' : 'transparent' }}
                          _hover={{ bg: chapter.isOpen ? (selectedNavChapter === cIdx ? 'blue.50' : 'gray.50') : 'transparent', _dark: { bg: chapter.isOpen ? (selectedNavChapter === cIdx ? 'blue.900' : 'gray.800') : 'transparent' } }}
                          borderBottom="1px solid" borderBottomColor={borderColor}
                          borderRight={selectedNavChapter === cIdx ? "4px solid" : "4px solid transparent"}
                          borderRightColor="blue.500"
                          onClick={() => {
                            if (chapter.isOpen) {
                              setSelectedNavChapter(cIdx);
                              setSelectedNavPart(null);
                              if (isMobile) setMobileActivePane('parts');
                            }
                          }}
                        >
                          <HStack justify="space-between">
                            <Text fontWeight="700" fontSize="sm" color={selectedNavChapter === cIdx ? "blue.700" : textColor} _dark={{ color: selectedNavChapter === cIdx ? "blue.200" : textColor }}>{chapter.nom}</Text>
                            {!chapter.isOpen ? <Icon as={FiLock} color="red.400" fontSize="sm" /> : <Icon as={FiChevronLeft} color={selectedNavChapter === cIdx ? "blue.500" : "gray.400"} />}
                          </HStack>
                          <Text fontSize="xs" color="gray.500" mt={1}>{chapter.parties?.reduce((acc, p) => acc + (p.videos?.length || 0), 0)} مقطع</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Column 2: Parts */}
              {selectedNavChapter !== null && courseData.chapitres?.[selectedNavChapter]?.parties && (!isMobile || mobileActivePane === 'parts') && (
                <Box
                  w={{ base: 'full', lg: '300px' }}
                  bg={cardBg}
                  display="flex"
                  flexDirection="column"
                  borderLeft={{ base: 'none', lg: "1px solid" }}
                  borderColor={borderColor}
                  h="auto"
                >
                  <Box p={4} borderBottom="1px solid" borderColor={borderColor} bg="gray.50" _dark={{ bg: "gray.800" }}>
                    <HStack>
                      {isMobile && (
                        <IconButton size="sm" variant="ghost" icon={<FiArrowRight />} onClick={() => setMobileActivePane('chapters')} aria-label="Back" />
                      )}
                      <Heading size="sm" color={textColor}>الأجزاء</Heading>
                    </HStack>
                  </Box>
                  <Box
                    flex="1"
                    overflowY="visible"
                    pt={2}
                    pb={{ base: '80px', lg: 0 }}
                  >
                    <VStack align="stretch" spacing={0}>
                      {courseData.chapitres[selectedNavChapter].parties.map((part, pIdx) => (
                        <Box
                          key={pIdx}
                          px={4} py={4}
                          cursor="pointer"
                          bg={selectedNavPart === pIdx ? 'blue.50' : 'transparent'}
                          _dark={{ bg: selectedNavPart === pIdx ? 'blue.900' : 'transparent' }}
                          _hover={{ bg: selectedNavPart === pIdx ? 'blue.50' : 'gray.50', _dark: { bg: selectedNavPart === pIdx ? 'blue.900' : 'gray.800' } }}
                          borderBottom="1px solid" borderBottomColor={borderColor}
                          borderRight={selectedNavPart === pIdx ? "4px solid" : "4px solid transparent"}
                          borderRightColor="blue.500"
                          onClick={() => {
                            setSelectedNavPart(pIdx);
                            if (isMobile) setMobileActivePane('videos');
                          }}
                        >
                          <HStack justify="space-between">
                            <Text fontWeight="700" fontSize="sm" color={selectedNavPart === pIdx ? "blue.700" : textColor} _dark={{ color: selectedNavPart === pIdx ? "blue.200" : textColor }}>{part.name}</Text>
                            <Icon as={FiChevronLeft} color={selectedNavPart === pIdx ? "blue.500" : "gray.400"} />
                          </HStack>
                          <Text fontSize="xs" color="gray.500" mt={1}>{part.videos?.length || 0} فيديو</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Column 3: Videos & Attachments */}
              {selectedNavChapter !== null && selectedNavPart !== null && courseData.chapitres?.[selectedNavChapter]?.parties?.[selectedNavPart] && (!isMobile || mobileActivePane === 'videos') && (
                <Box
                  w={{ base: 'full', lg: '340px' }}
                  bg={cardBg}
                  display="flex"
                  flexDirection="column"
                  h="auto"
                >
                  <Box p={4} borderBottom="1px solid" borderColor={borderColor} bg="white" _dark={{ bg: "gray.900" }}>
                    <HStack>
                      {isMobile && (
                        <IconButton size="sm" variant="ghost" icon={<FiArrowRight />} onClick={() => setMobileActivePane('parts')} aria-label="Back" />
                      )}
                      <Heading size="sm" color={textColor}>المقاطع والمرفقات</Heading>
                    </HStack>
                  </Box>
                  <Box
                    flex="1"
                    overflowY="visible"
                    pt={2}
                    pb={{ base: '80px', lg: 0 }}
                  >
                    <VStack align="stretch" spacing={0}>
                      {courseData.chapitres[selectedNavChapter].parties[selectedNavPart].videos?.map((video, vIdx) => {
                        const isSelected = selectedVideo && selectedVideo.chapterIndex === selectedNavChapter && selectedVideo.partIndex === selectedNavPart && selectedVideo.videoIndex === vIdx;
                        return (
                          <Box
                            key={vIdx} px={4} py={3} cursor="pointer" transition="all 0.2s"
                            bg={isSelected ? 'gray.50' : 'transparent'} _dark={{ bg: isSelected ? 'gray.700' : 'transparent' }}
                            _hover={{ bg: isSelected ? 'gray.100' : 'gray.50', _dark: { bg: isSelected ? 'gray.600' : 'gray.800' } }}
                            onClick={() => handleVideoSelect(video, selectedNavChapter, selectedNavPart, vIdx)}
                            borderBottom="1px solid" borderBottomColor={borderColor}
                            borderRight={isSelected ? "4px solid" : "4px solid transparent"} borderRightColor="blue.500"
                          >
                            <HStack align="flex-start" spacing={3}>
                              <Box mt={1} color={isSelected ? 'blue.500' : 'gray.400'}>
                                {completedVideos.has(`${selectedNavChapter}-${selectedNavPart}-${vIdx}`) ? <Icon as={FiCheckCircle} color="green.500" /> : <Icon as={isSelected ? FiPlay : FiPlayCircle} />}
                              </Box>
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight={isSelected ? "700" : "500"} color={isSelected ? 'blue.700' : textColor} _dark={{ color: isSelected ? 'blue.200' : textColor }} lineHeight="1.4">{video.title || video.nomvideo}</Text>
                                <HStack fontSize="xs" color="gray.500">
                                  <Icon as={FiClock} fontSize="x-small" /><Text fontSize="2xs">{video.duree || ""}</Text>
                                </HStack>
                              </VStack>
                            </HStack>
                          </Box>
                        );
                      })}

                      {courseData.chapitres[selectedNavChapter].parties[selectedNavPart].attachments?.length > 0 && (
                        <Box px={4} py={4} bg="gray.50" _dark={{ bg: "gray.800" }} borderTop="1px solid" borderColor={borderColor}>
                          <Text fontSize="xs" fontWeight="700" color="teal.600" mb={3}>مرفقات الجزء</Text>
                          <VStack align="stretch" spacing={2}>
                            {courseData.chapitres[selectedNavChapter].parties[selectedNavPart].attachments.map((att, aIdx) => (
                              <AttachmentItem key={aIdx} attachment={att} onDownload={handleAttachmentDownload} />
                            ))}
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                </Box>
              )}

            </Flex>

            {/* Player & Info Section */}
            {(!isMobile || mobileActivePane === 'player') && (
              <Box
                ref={playerRef}
                flex="1"
                overflowY="visible"
                p={{ base: 0, lg: 6 }}
                css={{ '&::-webkit-scrollbar': { width: '8px' }, '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' } }}
              >

                {isMobile && (
                  <Button m={4} leftIcon={<FiArrowRight />} colorScheme="blue" variant="outline" onClick={() => setMobileActivePane('videos')}>
                    العودة للمقاطع
                  </Button>
                )}

                <Box bg="black" w="full" position="relative" mb={{ base: 0, lg: 6 }} borderRadius={{ base: 0, lg: "xl" }} overflow="hidden" boxShadow="lg">
                  <AspectRatio ratio={16 / 9}>
                    {selectedVideo ? (
                      <VideoPlayer src={selectedVideo.url} poster={selectedVideo.thumbnail} idvdocipher={selectedVideo.idvdocipher} videoId={selectedVideo.idvdocipher} video={selectedVideo} style={{ width: "100%", height: "100%" }} />
                    ) : (
                      <Center bg="gray.900" color="white" flexDirection="column" gap={4}>
                        <Icon as={FiPlayCircle} boxSize={16} opacity={0.5} />
                        <Text fontSize={{ base: 'sm', md: 'lg' }} fontWeight="600" textAlign="center" px={4}>اختر فيديو من القائمة الجانبية للبدء</Text>
                      </Center>
                    )}
                  </AspectRatio>
                </Box>

                {/* Video Details & Tabs */}
                {selectedVideo && (
                  <Box mt={{ base: 0, lg: 0 }} bg={cardBg} borderRadius={{ base: 0, lg: "xl" }} border="1px solid" borderColor={borderColor} shadow="sm">
                    <Box p={{ base: 4, md: 6 }}>
                      <HStack justify="space-between" align="start" flexWrap="wrap" gap={4}>
                        <VStack align="flex-start" spacing={2} flex="1" minW={0}>
                          <Heading size={{ base: 'md', md: 'lg' }} color={textColor} fontWeight="700" noOfLines={2}>{selectedVideo.title || selectedVideo.nomvideo}</Heading>
                          <HStack color="gray.500" fontSize="sm" spacing={4}>
                            <HStack><Icon as={FiClock} /><Text>{selectedVideo.duree || ""}</Text></HStack>
                          </HStack>
                        </VStack>

                        <HStack flexShrink={0}>
                          <IconButton icon={<FiChevronRight />} onClick={handlePreviousVideo} variant="outline" colorScheme="blue" borderRadius="full" aria-label="السابق" />
                          <IconButton icon={<FiChevronLeft />} onClick={handleNextVideo} variant="solid" colorScheme="blue" borderRadius="full" aria-label="التالي" />
                        </HStack>
                      </HStack>

                      {/* 72h SMS Admin Button */}
                      {(user?.role === 'admin' || user?.role === 'instructor') && selectedVideo.createdAt && (Date.now() - new Date(selectedVideo.createdAt).getTime() >= 72 * 60 * 60 * 1000) && (
                        <Button mt={4} colorScheme="red" variant="solid" shadow="md" onClick={() => setIsSmsModalOpen(true)} leftIcon={<span>📱</span>} w={{ base: 'full', md: 'auto' }}>
                          إرسال رسالة SMS للغائبين
                        </Button>
                      )}
                    </Box>

                    <Divider borderColor={borderColor} />

                    {/* TABS */}
                    <Tabs colorScheme="blue" size="md" px={{ base: 2, md: 6 }}>
                      <TabList borderBottom="none" pt={2}>
                        <Tab fontWeight="600" color="gray.500" _selected={{ color: "blue.600", borderBottom: "2px solid" }}>تنقيل الفيديو</Tab>
                      </TabList>

                      <TabPanels>
                        <TabPanel px={{ base: 3, md: 0 }} py={6}>
                          {selectedVideo.description && (
                            <Box mb={6}>
                              <Text color="gray.600" _dark={{ color: "gray.300" }} lineHeight="1.8" fontSize="md">
                                {selectedVideo.description}
                              </Text>
                            </Box>
                          )}
                          {/* Student View */}
                          {user?.role === 'student' && (
                            <VStack align="stretch" spacing={6}>
                              {myHomeworkReply ? (
                                <Box p={4} bg="green.50" _dark={{ bg: "green.900" }} borderRadius="xl" border="1px solid" borderColor="green.200">
                                  <HStack mb={4}>
                                    <Icon as={FiCheckCircle} color="green.500" boxSize={5} />
                                    <Text color="green.700" _dark={{ color: "green.100" }} fontWeight="700">تم إرسال الواجب بنجاح</Text>
                                  </HStack>
                                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                    {myHomeworkReply.photoUrls.map((url, i) => (
                                      <Image
                                        key={i}
                                        src={`${process.env.REACT_APP_API_URL || 'https://api.cobra-education.com'}${url.startsWith('/') ? '' : '/'}${url}`}
                                        borderRadius="lg"
                                        h="100px"
                                        w="100%"
                                        objectFit="cover"
                                        shadow="sm"
                                        cursor="pointer"
                                        onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'https://api.cobra-education.com'}${url.startsWith('/') ? '' : '/'}${url}`, '_blank')}
                                      />
                                    ))}
                                  </SimpleGrid>
                                  <Text fontSize="xs" color="gray.500" mt={4} mb={2}>تاريخ التسليم: {new Date(myHomeworkReply.submittedAt).toLocaleString('ar-EG')}</Text>
                                  {myHomeworkReply.remarque && (
                                    <Box mt={3} p={3} bg="white" _dark={{ bg: "gray.800" }} borderRadius="md" borderLeft="4px solid" borderColor="blue.400">
                                      <Text fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }}>
                                        <Text as="span" fontWeight="bold" color="blue.500" mr={1}>ملاحظة الأستاذ:</Text>
                                        {myHomeworkReply.remarque}
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Box p={4} bg="orange.50" _dark={{ bg: "orange.900" }} borderRadius="xl" border="1px solid" borderColor="orange.200">
                                  <Text color="orange.700" _dark={{ color: "orange.100" }} fontWeight="600">⚠️ لم تقم بارسال تنقيل الفيديو كامل</Text>
                                </Box>
                              )}

                              <Box>
                                <Text fontSize="sm" color="red.500" fontWeight="700" mb={2}>
                                  الرجاء كتابة اسمك ولقبك على كل ورقة سترسلها
                                </Text>
                                <FormControl>
                                  <FormLabel fontWeight="600">
                                    رفع صور الواجب
                                    {homeworkPhotos.length > 0 && (
                                      <Badge ml={2} colorScheme="blue" borderRadius="full">
                                        {homeworkPhotos.length} / 10 صور
                                      </Badge>
                                    )}
                                  </FormLabel>

                                  {/* Drop zone / add button */}
                                  <Box
                                    as="label"
                                    htmlFor="hw-file-input"
                                    display="flex"
                                    flexDir="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    border="2px dashed"
                                    borderColor="blue.300"
                                    borderRadius="xl"
                                    p={5}
                                    bg="blue.50"
                                    _dark={{ bg: "blue.900", borderColor: "blue.600" }}
                                    cursor="pointer"
                                    _hover={{ bg: "blue.100", _dark: { bg: "blue.800" } }}
                                    transition="all 0.15s"
                                    mb={homeworkPhotos.length > 0 ? 3 : 0}
                                  >
                                    <input
                                      id="hw-file-input"
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      style={{ display: 'none' }}
                                      onChange={(e) => {
                                        const newFiles = Array.from(e.target.files);
                                        setHomeworkPhotos(prev => {
                                          const combined = [...prev, ...newFiles];
                                          return combined.slice(0, 10); // max 10
                                        });
                                        e.target.value = ''; // allow re-selecting same file
                                      }}
                                    />
                                    <Icon as={FiPaperclip} boxSize={6} color="blue.400" mb={2} />
                                    <Text fontSize="sm" fontWeight="600" color="blue.600" _dark={{ color: "blue.200" }}>
                                      {homeworkPhotos.length === 0 ? 'اختر الصور أو اسحب هنا' : 'إضافة صور أخرى'}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                      JPEG, PNG, WEBP — حتى 10 صور، 10 MB لكل صورة
                                    </Text>
                                  </Box>

                                  {/* Thumbnails grid */}
                                  {homeworkPhotos.length > 0 && (
                                    <SimpleGrid columns={{ base: 3, md: 5 }} spacing={2} mt={2}>
                                      {homeworkPhotos.map((file, idx) => {
                                        const url = URL.createObjectURL(file);
                                        return (
                                          <Box key={idx} position="relative" borderRadius="lg" overflow="hidden" h="80px">
                                            <Image
                                              src={url}
                                              h="80px"
                                              w="100%"
                                              objectFit="cover"
                                              borderRadius="lg"
                                              onLoad={() => URL.revokeObjectURL(url)}
                                            />
                                            <Box
                                              position="absolute"
                                              top="2px"
                                              right="2px"
                                              bg="red.500"
                                              color="white"
                                              borderRadius="full"
                                              w="18px"
                                              h="18px"
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                              cursor="pointer"
                                              fontSize="10px"
                                              fontWeight="bold"
                                              onClick={() => setHomeworkPhotos(prev => prev.filter((_, i) => i !== idx))}
                                              _hover={{ bg: "red.700" }}
                                            >
                                              ✕
                                            </Box>
                                            <Text
                                              position="absolute"
                                              bottom="0"
                                              left="0"
                                              right="0"
                                              bg="blackAlpha.600"
                                              color="white"
                                              fontSize="9px"
                                              textAlign="center"
                                              px={1}
                                              noOfLines={1}
                                            >
                                              {file.name}
                                            </Text>
                                          </Box>
                                        );
                                      })}
                                    </SimpleGrid>
                                  )}
                                </FormControl>

                                <HStack mt={4} spacing={3}>
                                  <Button
                                    colorScheme="blue"
                                    onClick={handleHomeworkSubmit}
                                    isLoading={isSubmittingHomework}
                                    isDisabled={!homeworkPhotos.length}
                                    leftIcon={<Icon as={FiPaperclip} />}
                                  >
                                    إرسال {homeworkPhotos.length > 0 ? `(${homeworkPhotos.length} صور)` : 'الواجب'}
                                  </Button>
                                  {homeworkPhotos.length > 0 && (
                                    <Button
                                      variant="ghost"
                                      colorScheme="red"
                                      size="sm"
                                      onClick={() => setHomeworkPhotos([])}
                                    >
                                      مسح الكل
                                    </Button>
                                  )}
                                </HStack>
                              </Box>
                            </VStack>
                          )}

                          {/* Admin/Teacher View */}
                          {(user?.role === 'admin' || user?.role === 'instructor') && (
                            <VStack align="stretch" spacing={4}>
                              <HStack justify="space-between">
                                <Text fontWeight="700" fontSize="lg">مشاركات الطلاب ({allHomeworkReplies.length})</Text>
                                <Button size="sm" variant="outline" colorScheme="blue" onClick={() => fetchHomeworkData(selectedVideo._id)} isLoading={isLoadingHomework}>تحديث</Button>
                              </HStack>

                              {allHomeworkReplies.length === 0 ? (
                                <Center py={8} bg="gray.50" _dark={{ bg: "gray.700" }} borderRadius="lg"><Text color="gray.500">لا يوجد ردود بعد</Text></Center>
                              ) : (
                                <SimpleGrid columns={{ base: 1 }} spacing={4}>
                                  {allHomeworkReplies.map(reply => (
                                    <Box key={reply._id} p={4} borderRadius="lg" border="1px solid" borderColor="gray.200" _dark={{ borderColor: "gray.600" }}>
                                      <HStack mb={3}>
                                        <Avatar size="sm" name={reply.studentId?.username} />
                                        <VStack align="start" spacing={0}>
                                          <Text fontWeight="700" fontSize="sm">{reply.studentId?.username}</Text>
                                          <Text fontSize="xs" color="gray.500">{new Date(reply.submittedAt).toLocaleString('ar-EG')}</Text>
                                        </VStack>
                                      </HStack>
                                      <SimpleGrid columns={{ base: 3, md: 5 }} spacing={2}>
                                        {reply.photoUrls.map((url, i) => (
                                          <Image
                                            key={i}
                                            src={`${process.env.REACT_APP_API_URL || 'https://api.cobra-education.com'}${url.startsWith('/') ? '' : '/'}${url}`}
                                            borderRadius="md"
                                            h="80px"
                                            w="100%"
                                            objectFit="cover"
                                            cursor="pointer"
                                            onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'https://api.cobra-education.com'}${url.startsWith('/') ? '' : '/'}${url}`, '_blank')}
                                          />
                                        ))}
                                      </SimpleGrid>
                                      {reply.remarque && (
                                        <Box mt={3} p={3} bg="white" _dark={{ bg: "gray.800" }} borderRadius="md" borderLeft="4px solid" borderColor="blue.400">
                                          <Text fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }}>
                                            <Text as="span" fontWeight="bold" color="blue.500" mr={1}>ملاحظة الأستاذ:</Text>
                                            {reply.remarque}
                                          </Text>
                                        </Box>
                                      )}
                                    </Box>
                                  ))}
                                </SimpleGrid>
                              )}
                            </VStack>
                          )}
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </Box>
                )}
              </Box>
            )}
          </Flex>

          {/* Mobile Bottom Navigation Bar */}
          {isMobile && (
            <Box
              position="fixed"
              bottom={0}
              left={0}
              right={0}
              bg="white"
              _dark={{ bg: "gray.800" }}
              borderTop="1px solid"
              borderColor={borderColor}
              display="flex"
              zIndex={100}
              shadow="0 -4px 12px rgba(0,0,0,0.08)"
            >
              {[
                { key: 'chapters', label: 'الفصول', icon: FiBook },
                { key: 'parts', label: 'الأجزاء', icon: FiLayers },
                { key: 'videos', label: 'الفيديوات', icon: FiPlay },
                { key: 'player', label: 'المشغّل', icon: FiPlayCircle },
              ].map(({ key, label, icon }) => (
                <Box
                  key={key}
                  flex="1"
                  py={3}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => setMobileActivePane(key)}
                  borderBottom={mobileActivePane === key ? '3px solid' : '3px solid transparent'}
                  borderBottomColor={mobileActivePane === key ? 'blue.500' : 'transparent'}
                  bg={mobileActivePane === key ? 'blue.50' : 'transparent'}
                  _dark={{ bg: mobileActivePane === key ? 'blue.900' : 'transparent' }}
                  transition="all 0.15s"
                >
                  <Icon
                    as={icon}
                    boxSize={5}
                    color={mobileActivePane === key ? 'blue.600' : 'gray.400'}
                    mb={1}
                  />
                  <Text
                    fontSize="10px"
                    fontWeight={mobileActivePane === key ? '700' : '500'}
                    color={mobileActivePane === key ? 'blue.600' : 'gray.500'}
                  >
                    {label}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Flex>

      {/* SMS Modal */}
      <Modal isOpen={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} isCentered dir="rtl" size="md">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader bg="red.500" color="white" borderTopRadius="xl" pt={4} pb={4} fontWeight="700">إرسال SMS للغائبين</ModalHeader>
          <ModalCloseButton color="white" top={3} left={3} right="auto" />
          <ModalBody py={6}>
            <Text mb={4}>سيتم إرسال تنبيه بالواجب للذين لم يسلموا. اختر الجهة:</Text>
            <VStack spacing={3}>
              <Button w="full" size="lg" colorScheme="blue" isLoading={isSendingSms} onClick={() => handleSendSms('students')}>إرسال للطلاب</Button>
              <Button w="full" size="lg" colorScheme="teal" isLoading={isSendingSms} onClick={() => handleSendSms('parents')}>إرسال لأولياء الأمور</Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default function CourseDetails() {
  return <CourseDetailsContent />;
}
